import {socialCount} from '@github-ui/code-view-sidebar/utils'
import {NotificationsSubscriptionsMenu} from '@github-ui/notifications-subscriptions-menu'
import {
  EyeIcon,
  GitBranchIcon,
  GlobeIcon,
  LawIcon,
  LinkIcon,
  LockIcon,
  MirrorIcon,
  PulseIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoTemplateIcon,
  StarIcon,
  TagIcon,
} from '@primer/octicons-react'
import {Button, IconButton, Link, Truncate} from '@primer/react'
import type {HeaderResponse, RepoPaths} from '../types'
import {StarButton} from '@github-ui/repo-star-button'

function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}

function repoTypeLabel(
  repo: Pick<HeaderResponse['repo'], 'isMirror' | 'isTemplate' | 'isPrivate' | 'visibilityLabel'>,
): {icon: typeof RepoIcon; text: string} {
  const {isMirror, isTemplate, isPrivate, visibilityLabel} = repo
  if (isMirror) return {icon: MirrorIcon, text: `${visibilityLabel} repository`}
  if (isTemplate) return {icon: RepoTemplateIcon, text: `${visibilityLabel} repository`}
  if (isPrivate) return {icon: LockIcon, text: `${visibilityLabel} repository`}
  return {icon: GlobeIcon, text: `${visibilityLabel} repository`}
}

export type RepoMobileDetailsProps = Pick<
  HeaderResponse,
  | 'description'
  | 'website'
  | 'stargazerCount'
  | 'forksCount'
  | 'watcherCount'
  | 'stargazersPath'
  | 'forkNetworkPath'
  | 'watchersPath'
  | 'showInsights'
  | 'ownerLogin'
  | 'repoName'
  | 'repo'
  | 'star'
  | 'fork'
  | 'watch'
  | 'viewer'
  | 'showSponsorButton'
  | 'showTemplateButton'
  | 'sections'
> & {
  paths: RepoPaths
}

/** A muted icon + optional bold count + label link used for repository stats. */
function StatLink({
  href,
  icon: Icon,
  count,
  label,
}: {
  href: string
  icon: React.ComponentType<{size?: number; className?: string}>
  count?: number
  label: string
}) {
  return (
    <Link muted href={href} className="d-block mr-2">
      <Icon size={16} className="mr-2" />
      {count !== undefined ? (
        <>
          <span className="text-bold">{socialCount(count)}</span> {label}
        </>
      ) : (
        label
      )}
    </Link>
  )
}

/** Mobile-only repository details shown below the title on narrow screens. */
export function RepoMobileDetails({
  repo,
  fork,
  star,
  watch,
  viewer,
  paths,
  showTemplateButton,
  description,
  website,
  stargazerCount,
  forksCount,
  watcherCount,
  stargazersPath,
  forkNetworkPath,
  watchersPath,
  showInsights,
  sections,
}: RepoMobileDetailsProps) {
  const repoNameWithOwner = paths.repoNameWithOwner

  const typeInfo = repoTypeLabel(repo)
  const tagCount = typeof sections.releases === 'object' ? sections.releases.tagCount : undefined

  return (
    <div className="d-block d-md-none" data-testid="repo-mobile-details">
      {/* Functional buttons row */}
      <div className="d-flex gap-2 tmp-mb-3 flex-wrap">
        <div className="d-flex flex-row gap-2" data-testid="responsive-social-buttons">
          {viewer.isLoggedIn && watch.canWatch && (
            <NotificationsSubscriptionsMenu
              repositoryId={watch.watchData.repositoryId}
              repositoryName={watch.watchData.repositoryName}
              watchersCount={watch.watchData.watchersCount}
              subscriptionType={watch.watchData.subscriptionType}
              subscribableThreadTypes={watch.watchData.subscribableThreadTypes}
              repositoryLabels={watch.watchData.repositoryLabels}
              showLabelSubscriptions={watch.watchData.showLabelSubscriptions}
              subscribedLabelIds={watch.watchData.subscribedLabelIds}
              subscribedLabels={[]}
            />
          )}
          <IconButton
            as="a"
            href={viewer.isLoggedIn ? paths.forkSelectPath : `/login?return_to=/${repoNameWithOwner}`}
            icon={RepoForkedIcon}
            size="small"
            aria-label={`Fork ${repoNameWithOwner}`}
            data-testid="mobile-fork-button"
            disabled={viewer.isLoggedIn && !fork.canFork}
          />

          <StarButton
            buttonType="icon-button"
            starred={star.viewerHasStarred}
            starCount={stargazerCount}
            starPath={paths.starPath}
            unstarPath={paths.unstarPath}
            canStar={star.canStar}
            isLoggedIn={viewer.isLoggedIn}
            listsApiPath={paths.listsApiPath}
            listsUpdatePath={paths.listsUpdatePath}
            repoNameWithOwner={paths.repoNameWithOwner}
            emuContributionBlocked={viewer.emuContributionBlocked}
          />
        </div>
        {showTemplateButton && repo.isTemplate && (
          <div className="d-flex flex-row gap-2">
            <Button
              as="a"
              href={paths.templatePath}
              variant="primary"
              size="small"
              leadingVisual={RepoTemplateIcon}
              data-testid="mobile-use-template-button"
            >
              Use this template
            </Button>
          </div>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="f4 tmp-mb-3 color-fg-muted" data-testid="mobile-description">
          {description}
        </p>
      )}

      {/* License */}
      {repo.license && (
        <div className="mb-2 d-flex flex-items-center">
          <LawIcon className="color-fg-muted flex-shrink-0 mr-2" />
          <span className="color-fg-muted">{repo.license.name}</span>
        </div>
      )}

      {/* Website */}
      {website && (
        <div className="mb-2 d-flex flex-items-center">
          <LinkIcon className="flex-shrink-0 mr-2" />

          <Truncate as="span" title={website} maxWidth="none">
            <Link href={website} target="_blank" rel="noopener noreferrer" className="text-bold">
              {website.replace(/^https?:\/\//, '')}
            </Link>
          </Truncate>
        </div>
      )}

      {/* Stats */}
      <ul className="d-flex flex-wrap mb-2 gap-2 list-style-none pl-0" data-testid="repo-responsive-details">
        <li>
          <StatLink
            href={stargazersPath}
            icon={StarIcon}
            count={stargazerCount}
            label={pluralize(stargazerCount, 'star')}
          />
        </li>
        <li>
          <StatLink
            href={forkNetworkPath}
            icon={RepoForkedIcon}
            count={forksCount}
            label={pluralize(forksCount, 'fork')}
          />
        </li>
        <li>
          <StatLink href={watchersPath} icon={EyeIcon} count={watcherCount} label="watching" />
        </li>
        <li>
          {/* TODO: Use the fetch from packages/repos-overview/components/OverviewContent.tsx  */}
          <StatLink href={paths.branchesPath} icon={GitBranchIcon} label="Branches" />
        </li>
        <li>
          <StatLink href={paths.tagsPath} icon={TagIcon} count={tagCount} label={pluralize(tagCount ?? 0, 'tag')} />
        </li>
        {showInsights && (
          <li>
            <StatLink href={`${paths.repoUrl}/pulse`} icon={PulseIcon} label="Activity" />
          </li>
        )}
      </ul>

      {/* Repository type indicator */}
      <div className="d-flex color-fg-muted flex-wrap" data-testid="repo-type-indicator">
        <span className="d-inline-flex flex-items-center flex-shrink-0 mr-1">
          <typeInfo.icon size={16} />
          <span className="ml-1">{typeInfo.text}</span>
        </span>
        {repo.isFork && repo.parentRepo && (
          <span>
            {' · Forked from '}
            <Link href={repo.parentRepo.url} inline>
              {repo.parentRepo.ownerLogin}/{repo.parentRepo.name}
            </Link>
          </span>
        )}
        {!repo.isFork && !repo.isMirror && repo.templateSource && (
          <span className="flex-shrink-0">
            {' · Generated from '}
            <Link href={repo.templateSource.url} inline>
              {repo.templateSource.ownerLogin}/{repo.templateSource.name}
            </Link>
          </span>
        )}
        {repo.isMirror && repo.mirrorUrl && (
          <span>
            {' · Mirrored from '}
            <Link href={repo.mirrorUrl} inline>
              {repo.mirrorUrl}
            </Link>
          </span>
        )}
      </div>
    </div>
  )
}
